import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase, getStoredUser } from "../supabase.js";
import { extractHashtags } from "../utils/hashtags";
import { notifyMentions } from "../utils/notifications";
import { friendlyError } from "../utils/errors";
import { UPLOAD_DRAFT_KEY as DRAFT_KEY } from "../utils/storageKeys";
import { DESIGN_FILES_BUCKET } from "../utils/designFiles";
import ImageCropModal from "../components/ImageCropModal";
import HashtagInput from "../components/HashtagInput";
import { useLanguage } from "../context/LanguageContext";
import {
  IoLockClosedOutline,
  IoWarningOutline,
  IoCloudUploadOutline,
  IoCheckmarkCircle,
  IoCreateOutline,
  IoClose,
  IoCameraOutline,
  IoShirtOutline,
} from "react-icons/io5";

const EMBROIDERY_ACCEPT =
  ".dst,.pes,.jef,.exp,.vp3,.xxx,.hus,.pcs,.sew,.vip,.csd,.emb,.dsz,.tap";

const MAX_DESIGN_FILES = 12;
const MAX_IMAGES = 5;
const TITLE_LIMIT = 500;
// Client-side caps so an oversized pick fails fast with a clear message
// instead of sitting through a slow upload that Storage may reject anyway —
// not a substitute for a server-side bucket file-size limit, since this
// check is trivially bypassed by anyone calling the API directly.
const MAX_IMAGE_MB = 10;
const MAX_DESIGN_FILE_MB = 20;

export default function Upload() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();

  const [user, setUser] = useState(getStoredUser);
  const [profileUsername, setProfileUsername] = useState(null);

  // Arriving via a design's own "I made this!" link (?inspired_by=<id>) —
  // fetched for a human-readable confirmation banner rather than posting a
  // bare id with nothing to show the person what they're crediting. Read
  // fresh from location.search on every render (not frozen in state on
  // mount) because the "/upload" route element isn't remounted by React
  // Router when only the search string changes (e.g. landing on plain
  // "/upload" via the nav bar first, then later tag-icon-linking here with
  // "?inspired_by=..." in the same SPA session) — a one-time useState/[]
  // effect would silently keep whatever (or nothing) it read the first time.
  const inspiredByDesignId = new URLSearchParams(location.search).get("inspired_by");
  const [inspiredByDismissed, setInspiredByDismissed] = useState(false);
  const [inspiredBySource, setInspiredBySource] = useState(null);

  useEffect(() => {
    setInspiredByDismissed(false);
    if (!inspiredByDesignId) {
      setInspiredBySource(null);
      return;
    }
    let cancelled = false;
    supabase
      .from("designs")
      .select("id, title, users(username)")
      .eq("id", inspiredByDesignId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setInspiredBySource(data || null);
      });
    return () => {
      cancelled = true;
    };
  }, [inspiredByDesignId]);

  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [imageQueue, setImageQueue] = useState([]); // raw picked files waiting to be cropped
  const [editingIndex, setEditingIndex] = useState(null); // index in `images` being re-cropped, or null for a new photo
  const [designFiles, setDesignFiles] = useState([]);
  // Shape (width / height) of the post's first photo — the later ones start their crop on it.
  const [postAspect, setPostAspect] = useState(null);

  const [title, setTitle] = useState(() => {
    try {
      return localStorage.getItem(DRAFT_KEY) || "";
    } catch {
      return "";
    }
  });

  const [loading, setLoading] = useState(false);
  const [progressText, setProgressText] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const [imageDragOver, setImageDragOver] = useState(false);
  const [filesDragOver, setFilesDragOver] = useState(false);

  const [cropSrc, setCropSrc] = useState(null); // object URL of the image currently being cropped
  const [cropFileName, setCropFileName] = useState("photo.jpg");
  // closeCropModal() revokes cropSrc, but only when it's called from the
  // modal's own Cancel/Save — leaving the page mid-crop (e.g. tapping a
  // bottom-nav tab) unmounts this component without ever calling it,
  // leaking that blob for the rest of the SPA session.
  const cropSrcRef = useRef(cropSrc);
  cropSrcRef.current = cropSrc;
  useEffect(() => {
    return () => {
      if (cropSrcRef.current) URL.revokeObjectURL(cropSrcRef.current);
    };
  }, []);

  const imageInputRef = useRef(null);
  const filesInputRef = useRef(null);
  const uploadingRef = useRef(false);

  useEffect(() => {
    async function getUser() {
      const {
        data: { session }
      } = await supabase.auth.getSession();

      const currentUser = session?.user ?? null;
      setUser(currentUser);

      if (currentUser) {
        const { data } = await supabase
          .from("users")
          .select("username")
          .eq("id", currentUser.id)
          .single();
        setProfileUsername(data?.username || null);
      }
    }

    getUser();
  }, []);

  // Caption-only draft (photos/files are transient File objects, not worth
  // persisting) — saved as you type so leaving mid-post and coming back
  // doesn't lose what was written.
  useEffect(() => {
    try {
      if (title) localStorage.setItem(DRAFT_KEY, title);
      else localStorage.removeItem(DRAFT_KEY);
    } catch {}
  }, [title]);

  useEffect(() => {
    const urls = images.map(img => URL.createObjectURL(img));
    setImagePreviews(urls);

    return () => urls.forEach(url => URL.revokeObjectURL(url));
  }, [images]);

  // Crop one photo at a time: once the modal closes with nothing queued,
  // pop the next raw file off the queue and open the modal for it.
  useEffect(() => {
    if (cropSrc || imageQueue.length === 0) return;

    const next = imageQueue[0];
    setImageQueue(prev => prev.slice(1));
    setEditingIndex(null);
    setCropFileName(next.name || "photo.jpg");
    setCropSrc(URL.createObjectURL(next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageQueue, cropSrc]);

  if (!user) {
    return (
      <p style={{ padding: 20 }}>
        <IoLockClosedOutline style={{ verticalAlign: -2, marginRight: 6 }} />
        Please log in to upload designs.
      </p>
    );
  }

  function pickImages(fileList) {
    // Guards click, drag-drop AND the "add another photo" tile in one place
    // — an add/remove/re-crop landing mid-upload would only ever change the
    // preview grid, since handleUpload already closed over the array as it
    // was the moment Upload was clicked. Without this, removing a photo
    // while it was uploading looked like it worked (it vanished from the
    // grid) but that photo still made it into the finished post.
    if (loading) return;

    const incoming = Array.from(fileList || []).filter(f => f.type.startsWith("image/"));
    if (incoming.length === 0) {
      setError("Please choose image files for the photos.");
      return;
    }

    if (images.length + imageQueue.length + incoming.length > MAX_IMAGES) {
      setError(`You can upload up to ${MAX_IMAGES} photos per post.`);
      return;
    }

    const tooLarge = incoming.find(f => f.size > MAX_IMAGE_MB * 1024 * 1024);
    if (tooLarge) {
      setError(`"${tooLarge.name}" is over ${MAX_IMAGE_MB}MB. Please choose a smaller photo.`);
      return;
    }

    setError(null);
    setImageQueue(prev => [...prev, ...incoming]);
  }

  function reEditImage(index) {
    if (loading) return;
    const img = images[index];
    if (!img) return;
    setEditingIndex(index);
    setCropFileName(img.name || "photo.jpg");
    setCropSrc(URL.createObjectURL(img));
  }

  function closeCropModal() {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setEditingIndex(null);
  }

  function handleCropSave(croppedFile, shape) {
    if (editingIndex !== null) {
      setImages(prev => prev.map((img, i) => (i === editingIndex ? croppedFile : img)));
    } else {
      if (images.length === 0 && shape) setPostAspect(shape);
      setImages(prev => [...prev, croppedFile]);
    }
    closeCropModal();
  }

  function removeImage(index) {
    if (loading) return;
    if (images.length === 1) setPostAspect(null);
    setImages(prev => prev.filter((_, i) => i !== index));
  }

  function addDesignFiles(incomingFiles) {
    if (loading) return;

    const all = Array.from(incomingFiles);
    if (all.length === 0) return;

    // The input's accept="" attribute only filters what the OS file picker
    // shows — it does nothing for drag-and-drop, which bypasses it entirely.
    // Without this, dropping a file here skipped type checking altogether.
    const extensions = EMBROIDERY_ACCEPT.split(",");
    const incoming = all.filter(f => extensions.some(ext => f.name.toLowerCase().endsWith(ext)));
    if (incoming.length === 0) {
      setError(`Please choose embroidery design files (${EMBROIDERY_ACCEPT}).`);
      return;
    }

    const combined = [...designFiles, ...incoming];

    if (combined.length > MAX_DESIGN_FILES) {
      setError(`You can upload up to ${MAX_DESIGN_FILES} design files per post`);
      return;
    }

    const tooLarge = incoming.find(f => f.size > MAX_DESIGN_FILE_MB * 1024 * 1024);
    if (tooLarge) {
      setError(`"${tooLarge.name}" is over ${MAX_DESIGN_FILE_MB}MB. Please choose a smaller file.`);
      return;
    }

    setError(null);
    setDesignFiles(combined);
  }

  function removeDesignFile(index) {
    if (loading) return;
    setDesignFiles(prev => prev.filter((_, i) => i !== index));
  }

  // Strips "/" and ".." from a user-picked file's name before it becomes
  // part of a Storage path — a normal file picker/drag can't produce a
  // filename containing those (the OS itself forbids "/"), but a `File`
  // object built by hand (e.g. from the browser console) can carry any
  // string, and with upsert:true an unsanitized name could nest into or
  // collide with an unrelated path instead of staying inside images/raw.
  function sanitizeFileName(name) {
    return (name || "file").replace(/[/\\]/g, "_").replace(/\.\./g, "_");
  }

  async function uploadImageToSupabase(file) {
    const fileName = `${Date.now()}-${sanitizeFileName(file.name)}`;
    const path = `images/${fileName}`;

    const { error } = await supabase.storage
      .from("designs")
      .upload(path, file, {
        contentType: file.type || "image/jpeg",
        // Supabase Storage sends "Cache-Control: no-cache" (revalidate every
        // request) unless told otherwise — the timestamp in `fileName` above
        // already makes every upload its own path, so a photo is never
        // overwritten in place and a year-long cache is safe.
        cacheControl: "31536000",
        upsert: true,
      });

    if (error) {
      throw new Error(error.message || "Image upload failed");
    }

    const {
      data: { publicUrl },
    } = supabase.storage
      .from("designs")
      .getPublicUrl(path);

    return { url: publicUrl, path };
  }

  // Design files go to the private bucket, in a folder named after the uploader (the
  // bucket's rules only let you add to your own folder). No public link is made: what
  // is saved is the path, and downloads use a short-lived signed link.
  async function uploadDesignFile(file) {
    const unique = `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    // Storage refuses some characters in a path (# [ ] and many non-English letters), so
    // the path keeps only safe ones. The real name is saved beside it (as "name") and is
    // what the file is called when downloaded.
    const safeName = sanitizeFileName(file.name).replace(/[^A-Za-z0-9._-]+/g, "_");
    const path = `${user.id}/${unique}-${safeName}`;

    const { error } = await supabase.storage
      .from(DESIGN_FILES_BUCKET)
      .upload(path, file, {
        contentType: "application/octet-stream",
        upsert: false,
      });

    if (error) {
      throw new Error(error.message || `Failed to upload ${file.name}`);
    }

    return { name: file.name, path };
  }

  function resetForm() {
    setImages([]);
    setPostAspect(null);
    setDesignFiles([]);
    setTitle("");
    try {
      localStorage.removeItem(DRAFT_KEY);
    } catch {}
    if (imageInputRef.current) imageInputRef.current.value = "";
    if (filesInputRef.current) filesInputRef.current.value = "";
  }

  async function handleUpload() {
    setError(null);

    // A photo is all a post needs — the caption is optional.
    if (images.length === 0) {
      setError(t("upload.needPhoto"));
      return;
    }

    // disabled={loading} only takes effect on the next render — a very fast
    // double-click/double-tap can fire this twice before that paints, which
    // would otherwise upload every photo/file and insert the post twice.
    if (uploadingRef.current) return;
    uploadingRef.current = true;

    // Tracked outside the try body so the catch block below can still see
    // whatever made it to Storage before the failure, no matter which step
    // (a later file, or the insert itself) is what actually threw.
    const uploadedPaths = []; // photos, in the public bucket
    const uploadedFilePaths = []; // design files, in the private bucket

    try {
      setLoading(true);
      setSuccess(false);

      const imageURLs = [];
      for (let i = 0; i < images.length; i++) {
        setProgressText(
          images.length > 1
            ? `Uploading photo ${i + 1} of ${images.length}…`
            : "Uploading preview image…"
        );
        const { url, path } = await uploadImageToSupabase(images[i]);
        imageURLs.push(url);
        uploadedPaths.push(path);
      }

      const uploadedFiles = [];
      for (let i = 0; i < designFiles.length; i++) {
        setProgressText(`Uploading design file ${i + 1} of ${designFiles.length}…`);
        const uploaded = await uploadDesignFile(designFiles[i]);
        uploadedFiles.push({ name: uploaded.name, path: uploaded.path });
        uploadedFilePaths.push(uploaded.path);
      }

      setProgressText("Saving post…");

      const { data: inserted, error: insertError } = await supabase
        .from("designs")
        .insert([
          {
            title: title.trim(),
            hashtags: extractHashtags(title),
            image_url: imageURLs[0],
            images: imageURLs,
            design_url: null, // only older posts have a public file link
            design_files: uploadedFiles,
            user_id: user.id,
            username: profileUsername || user.email.split("@")[0],
            likes: 0,
            downloads: 0,
            // Omitted entirely (not just set to null) when there's nothing to
            // credit — PostgREST rejects the whole insert with an unknown-
            // column error if inspired_by_design_id is referenced at all
            // before the migration adding it has run, so every upload would
            // break, not just the ones arriving via an "I made this!" link.
            ...(!inspiredByDismissed && inspiredByDesignId ? { inspired_by_design_id: inspiredByDesignId } : {}),
          },
        ])
        .select("id")
        .single();

      if (insertError) {
        throw new Error(insertError.message || "Database save failed");
      }

      notifyMentions({ text: title, actorId: user.id, designId: inserted?.id });

      setSuccess(true);
      resetForm();
    } catch (err) {
      console.log(err);
      setError(friendlyError(err, "Upload failed. Please try again."));

      // A failure partway through (a later file, or the insert itself)
      // otherwise leaves everything uploaded so far orphaned in Storage —
      // nothing in the DB ever points to it, so it just sits there taking
      // up space. Best-effort: a cleanup hiccup shouldn't surface as a
      // second error on top of the one already shown above.
      if (uploadedPaths.length > 0) {
        supabase.storage
          .from("designs")
          .remove(uploadedPaths)
          .then(({ error: cleanupError }) => {
            if (cleanupError) console.log("UPLOAD CLEANUP ERROR:", cleanupError);
          });
      }
      if (uploadedFilePaths.length > 0) {
        supabase.storage
          .from(DESIGN_FILES_BUCKET)
          .remove(uploadedFilePaths)
          .then(({ error: cleanupError }) => {
            if (cleanupError) console.log("UPLOAD CLEANUP ERROR:", cleanupError);
          });
      }
    } finally {
      uploadingRef.current = false;
      setLoading(false);
      setProgressText("");
    }
  }

  const canPost = images.length > 0 && !loading;

  return (
    <div style={page}>
      <div style={headerRow}>
        <span style={headerIcon} aria-hidden="true">
          <IoCloudUploadOutline size={22} />
        </span>
        <div style={{ minWidth: 0 }}>
          <h2 style={heading}>{t("upload.heading")}</h2>
          <p style={subtitle}>{t("upload.subtitle")}</p>
        </div>
      </div>

      {error && (
        <div style={errorBanner} role="alert">
          <IoWarningOutline style={{ marginRight: 8, flexShrink: 0, fontSize: 16 }} />
          {error}
        </div>
      )}

      {success && (
        <div style={successBanner}>
          <IoCheckmarkCircle style={{ verticalAlign: -2, marginRight: 6 }} />
          {t("upload.uploaded")}
          <div style={successActions}>
            <button style={secondaryBtn} onClick={() => setSuccess(false)}>
              {t("upload.uploadAnother")}
            </button>
            <button style={primaryBtnSmall} onClick={() => navigate("/")}>
              {t("upload.goHome")}
            </button>
          </div>
        </div>
      )}

      {inspiredByDesignId && inspiredBySource && !inspiredByDismissed && (
        <div style={inspiredByBanner}>
          <span>
            🧵 Crediting <strong>@{inspiredBySource.users?.username || "unknown"}</strong>'s design
            {inspiredBySource.title ? ` "${inspiredBySource.title}"` : ""} as your inspiration
          </span>
          <button type="button" style={inspiredByDismissBtn} onClick={() => setInspiredByDismissed(true)}>
            <IoClose size={16} />
          </button>
        </div>
      )}

      {/* ===== Preview photos drop zone (multiple, swipeable once posted) ===== */}
      <section style={card}>
        <div style={cardHead}>
          <span style={cardTitle}>{t("upload.photos")}</span>
          <span style={countChip}>{images.length}/{MAX_IMAGES}</span>
        </div>
        <div
          style={{
            ...dropZone,
            ...(imagePreviews.length > 0 ? dropZoneFilled : {}),
            ...(imageDragOver ? dropZoneActive : {}),
          }}
          role={imagePreviews.length === 0 ? "button" : undefined}
          tabIndex={imagePreviews.length === 0 ? 0 : undefined}
          aria-label={imagePreviews.length === 0 ? t("upload.photosHint") : undefined}
          onClick={() => imagePreviews.length === 0 && imageInputRef.current?.click()}
          onKeyDown={e => {
            if (imagePreviews.length === 0 && (e.key === "Enter" || e.key === " ")) {
              e.preventDefault();
              imageInputRef.current?.click();
            }
          }}
          onDragOver={e => {
            e.preventDefault();
            setImageDragOver(true);
          }}
          onDragLeave={() => setImageDragOver(false)}
          onDrop={e => {
            e.preventDefault();
            setImageDragOver(false);
            pickImages(e.dataTransfer.files);
          }}
        >
          {imagePreviews.length > 0 ? (
            <div style={photoGrid}>
              {imagePreviews.map((url, i) => (
                <div key={i} style={imagePreviewWrap}>
                  <img src={url} alt={`Preview ${i + 1}`} style={imagePreviewImg} />
                  {i === 0 && <span style={coverChip}>{t("upload.cover")}</span>}
                  <button
                    type="button"
                    style={editImageBtn}
                    disabled={loading}
                    onClick={e => {
                      e.stopPropagation();
                      reEditImage(i);
                    }}
                    aria-label={`Edit photo ${i + 1}`}
                  >
                    <IoCreateOutline size={12} />
                  </button>
                  <button
                    type="button"
                    style={removeImageBtn}
                    disabled={loading}
                    onClick={e => {
                      e.stopPropagation();
                      removeImage(i);
                    }}
                    aria-label={`Remove photo ${i + 1}`}
                  >
                    <IoClose size={13} />
                  </button>
                </div>
              ))}
              {images.length < MAX_IMAGES && (
                <button
                  type="button"
                  style={addMoreTile}
                  disabled={loading}
                  onClick={e => {
                    e.stopPropagation();
                    imageInputRef.current?.click();
                  }}
                  aria-label="Add another photo"
                >
                  <IoCameraOutline size={22} />
                </button>
              )}
            </div>
          ) : (
            <div style={emptyZone}>
              <span style={emptyIcon}>
                <IoCameraOutline size={24} />
              </span>
              <span style={emptyTitle}>{t("upload.photosHint")}</span>
              <span style={emptySub}>{t("upload.photosSub")}</span>
            </div>
          )}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            multiple
            disabled={loading}
            style={{ display: "none" }}
            onChange={e => {
              pickImages(e.target.files);
              e.target.value = "";
            }}
          />
        </div>
      </section>

      {/* ===== Embroidery design files drop zone (up to 12) ===== */}
      <section style={card}>
        <div style={cardHead}>
          <span style={cardTitle}>
            {t("upload.files")} <span style={optionalTag}>· {t("upload.optional")}</span>
          </span>
          <span style={countChip}>{designFiles.length}/{MAX_DESIGN_FILES}</span>
        </div>
        <div
          style={{ ...dropZone, ...(filesDragOver ? dropZoneActive : {}) }}
          role="button"
          tabIndex={0}
          aria-label={t("upload.filesHint")}
          onClick={() => filesInputRef.current?.click()}
          onKeyDown={e => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              filesInputRef.current?.click();
            }
          }}
          onDragOver={e => {
            e.preventDefault();
            setFilesDragOver(true);
          }}
          onDragLeave={() => setFilesDragOver(false)}
          onDrop={e => {
            e.preventDefault();
            setFilesDragOver(false);
            addDesignFiles(e.dataTransfer.files);
          }}
        >
          <div style={emptyZoneRow}>
            <span style={emptyIconSmall}>
              <IoShirtOutline size={20} />
            </span>
            <span style={{ minWidth: 0, textAlign: "left" }}>
              <span style={emptyTitle}>{t("upload.filesHint")}</span>
              <span style={{ ...emptySub, display: "block" }}>{t("upload.filesSub")}</span>
            </span>
          </div>
          <input
            ref={filesInputRef}
            type="file"
            accept={EMBROIDERY_ACCEPT}
            multiple
            disabled={loading}
            style={{ display: "none" }}
            onChange={e => {
              addDesignFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>

        {designFiles.length > 0 && (
          <ul style={fileList}>
            {designFiles.map((f, i) => (
              <li key={i} style={fileListItem}>
                <span style={fileListName}>
                  <IoShirtOutline style={{ verticalAlign: -2, marginRight: 6 }} />
                  {f.name}
                </span>
                <button
                  type="button"
                  style={fileRemoveBtn}
                  disabled={loading}
                  onClick={() => removeDesignFile(i)}
                  aria-label={`Remove ${f.name}`}
                >
                  <IoClose size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ===== Caption — optional (hashtags typed inline, like Instagram) ===== */}
      <section style={card}>
        <div style={cardHead}>
          <span style={cardTitle}>
            {t("upload.caption")} <span style={optionalTag}>· {t("upload.optional")}</span>
          </span>
          <span style={countChip}>{title.length}/{TITLE_LIMIT}</span>
        </div>
        <HashtagInput
          multiline
          rows={4}
          placeholder={t("upload.captionPlaceholder")}
          disabled={loading}
          value={title}
          maxLength={TITLE_LIMIT}
          onChange={setTitle}
        />
      </section>

      {/* ===== Upload Button ===== */}
      <button
        onClick={handleUpload}
        disabled={!canPost}
        style={{ ...primaryBtn, ...(canPost ? {} : primaryBtnOff) }}
      >
        {loading ? progressText || t("upload.uploading") : t("upload.uploadButton")}
      </button>
      {images.length === 0 && !loading && <p style={needHint}>{t("upload.needPhoto")}</p>}

      {cropSrc && (
        <ImageCropModal
          imageSrc={cropSrc}
          fileName={cropFileName}
          initialAspect={editingIndex === null ? postAspect : null}
          onCancel={closeCropModal}
          onSave={handleCropSave}
        />
      )}
    </div>
  );
}

/* ===== STYLES (Virsa palette, matches Home/Profile/Comments) ===== */

const page = {
  padding: "20px 16px calc(90px + env(safe-area-inset-bottom))",
  maxWidth: 520,
  margin: "0 auto",
  background: "var(--bg-page)",
  minHeight: "100vh",
};

const headerRow = { display: "flex", alignItems: "center", gap: 12, marginBottom: 18 };

const headerIcon = {
  flexShrink: 0,
  width: 44,
  height: 44,
  borderRadius: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--bg-soft)",
  color: "var(--accent-primary)",
};

const heading = { margin: 0, fontSize: 20, lineHeight: 1.2, color: "var(--text-heading)" };

const subtitle = { margin: "3px 0 0", fontSize: 13, color: "var(--text-muted)" };

const card = {
  background: "var(--bg-surface)",
  border: "1px solid var(--border-soft)",
  borderRadius: 16,
  padding: 14,
  marginBottom: 14,
};

const cardHead = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  marginBottom: 10,
};

const cardTitle = { fontSize: 14, fontWeight: 800, color: "var(--text-heading)" };

const optionalTag = { fontWeight: 600, fontSize: 12, color: "var(--text-muted)" };

const countChip = {
  flexShrink: 0,
  fontSize: 11.5,
  fontWeight: 700,
  color: "var(--text-muted)",
  background: "var(--bg-soft)",
  borderRadius: 999,
  padding: "3px 9px",
};

const dropZone = {
  border: "2px dashed var(--border-input)",
  borderRadius: 14,
  padding: "18px 14px",
  textAlign: "center",
  cursor: "pointer",
  background: "var(--bg-page)",
  transition: "border-color 0.15s, background 0.15s",
};

// Once photos are in, the box is just a tray for them — solid, tighter.
const dropZoneFilled = {
  border: "1.5px solid var(--border-soft)",
  padding: 12,
  cursor: "default",
};

const dropZoneActive = {
  border: "2px dashed var(--accent-primary)",
  background: "var(--bg-soft)",
};

const emptyZone = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 4,
};

const emptyZoneRow = { display: "flex", alignItems: "center", justifyContent: "center", gap: 12 };

const emptyIcon = {
  width: 52,
  height: 52,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--bg-soft)",
  color: "var(--accent-primary)",
  marginBottom: 6,
};

const emptyIconSmall = {
  flexShrink: 0,
  width: 40,
  height: 40,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--bg-soft)",
  color: "var(--accent-primary)",
};

const emptyTitle = { fontSize: 14, fontWeight: 700, color: "var(--text-secondary)" };

const emptySub = { fontSize: 12, color: "var(--text-muted)" };

const photoGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(76px, 1fr))",
  gap: 10,
};

const imagePreviewWrap = { position: "relative", aspectRatio: "1 / 1" };

const imagePreviewImg = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: 12,
  display: "block",
  background: "var(--border-soft)",
};

const coverChip = {
  position: "absolute",
  left: 5,
  top: 5,
  padding: "2px 7px",
  borderRadius: 999,
  fontSize: 10.5,
  fontWeight: 800,
  color: "#fff",
  background: "rgba(20, 14, 8, 0.65)",
  pointerEvents: "none",
};

const addMoreTile = {
  aspectRatio: "1 / 1",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 12,
  border: "2px dashed var(--border-input)",
  background: "var(--bg-surface)",
  color: "var(--text-muted)",
  cursor: "pointer",
};

const removeImageBtn = {
  position: "absolute",
  top: -7,
  right: -7,
  width: 26,
  height: 26,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  border: "2px solid var(--bg-surface)",
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  cursor: "pointer",
  lineHeight: 1,
  padding: 0,
};

const editImageBtn = {
  position: "absolute",
  bottom: 5,
  right: 5,
  width: 24,
  height: 24,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  border: "none",
  background: "rgba(20, 14, 8, 0.75)",
  color: "#fff",
  cursor: "pointer",
  padding: 0,
};

const fileList = { listStyle: "none", margin: "10px 0 0", padding: 0 };

const fileListItem = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  padding: "8px 12px",
  borderRadius: 10,
  background: "var(--bg-page)",
  border: "1px solid var(--border-soft)",
  marginBottom: 6,
};

const fileListName = {
  fontSize: 13,
  color: "var(--text-heading)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const fileRemoveBtn = {
  display: "flex",
  background: "none",
  border: "none",
  color: "var(--accent-primary)",
  cursor: "pointer",
  flexShrink: 0,
  padding: 4,
};

const primaryBtn = {
  width: "100%",
  height: 52,
  marginTop: 6,
  borderRadius: 14,
  border: "none",
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  fontWeight: 800,
  fontSize: 15.5,
  cursor: "pointer",
  transition: "opacity 0.15s",
};

const primaryBtnOff = { opacity: 0.45, cursor: "not-allowed" };

const needHint = {
  margin: "10px 0 0",
  textAlign: "center",
  fontSize: 12.5,
  color: "var(--text-muted)",
};

const primaryBtnSmall = {
  padding: "8px 16px",
  borderRadius: 10,
  border: "none",
  background: "var(--accent-primary)",
  color: "var(--accent-contrast)",
  fontWeight: 700,
  fontSize: 13,
  cursor: "pointer",
};

const secondaryBtn = {
  padding: "8px 16px",
  borderRadius: 10,
  border: "1.5px solid var(--accent-success)",
  background: "none",
  color: "var(--accent-success)",
  fontWeight: 700,
  fontSize: 13,
  cursor: "pointer",
};

const errorBanner = {
  display: "flex",
  alignItems: "center",
  background: "var(--alert-error-bg)",
  border: "1px solid var(--alert-error-border)",
  color: "var(--alert-error-text)",
  padding: "10px 14px",
  borderRadius: 12,
  fontSize: 13.5,
  marginBottom: 14,
};

const successBanner = {
  background: "var(--alert-success-bg)",
  border: "1px solid var(--alert-success-border)",
  color: "var(--alert-success-text)",
  padding: "12px 14px",
  borderRadius: 12,
  fontSize: 13.5,
  marginBottom: 14,
};

const successActions = { display: "flex", gap: 8, marginTop: 10 };

const inspiredByBanner = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 10,
  background: "var(--bg-soft)",
  border: "1px solid var(--accent-gold)",
  color: "var(--text-body)",
  padding: "10px 14px",
  borderRadius: 12,
  fontSize: 13,
  marginBottom: 14,
};

const inspiredByDismissBtn = {
  background: "none",
  border: "none",
  color: "var(--text-muted)",
  cursor: "pointer",
  padding: 4,
  flexShrink: 0,
  display: "flex",
};
